import React, { useState, useEffect } from 'react';
import {
  Modal,
  Text,
  Avatar,
  Box,
  Stack,
  Group,
  Badge,
  Paper,
  Button,
  Loader,
  Center,
  ScrollArea,
  Grid,
  Progress,
  RingProgress,
  ThemeIcon,
} from '@mantine/core';
import {
  IconUser,
  IconLogin,
  IconLogout,
  IconClock,
  IconAlertCircle,
  IconCalendarTime,
  IconHome,
  IconCalendar,
  IconCreditCard,
  IconAlertTriangle,
  IconExclamationCircle,
} from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDate } from '@/shared/utils/date';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import type {
  OvertimeAttendanceResponse,
  MonthlyAttendanceCountResponse,
  CustomerContractResponse,
  RecentAttendanceHistoryResponse,
} from '@/features/attendance/types/attendance';

interface DetailedAttendanceModalProps {
  opened: boolean;
  onClose: () => void;
  customer: {
    id: number;
    name: string;
    name_kana: string;
    photo_url?: string;
    avatar?: string;
    birth_date?: string;
    gender?: 'male' | 'female' | 'other';
    status: 'active' | 'paused' | 'withdrawn';
    memo?: string;
    store: {
      id: number;
      name: string;
    };
  } | null;
  attendance?: {
    action: 'check_in' | 'check_out';
    check_in_time?: string;
    check_out_time?: string;
    stay_duration?: string;
    is_overtime?: boolean;
  };
}

export const DetailedAttendanceModal: React.FC<DetailedAttendanceModalProps> = ({
  opened,
  onClose,
  customer,
  attendance,
}) => {
  const [countdown, setCountdown] = useState<number | null>(null);
  const { user } = useAuth();
  // 担当校を持たないユーザー（本社管理者ロール等）は従来通り全校合算で取得する
  const currentStoreId = user?.store_id;

  // データ取得（利用校＝現在ログイン中スタッフの担当校で絞込）
  const { data: overtimeData } = useQuery<OvertimeAttendanceResponse>({
    queryKey: ['overtime-attendance', customer?.id, currentStoreId],
    queryFn: async () => {
      if (!customer?.id) throw new Error('Customer ID is required');
      const response = await apiClient.get<OvertimeAttendanceResponse>(
        `/attendance/overtime-count/${customer.id}`,
        { params: { store_id: currentStoreId } }
      );
      return response.data;
    },
    enabled: opened && !!customer?.id,
    staleTime: 1000 * 60,
  });

  const { data: monthlyData } = useQuery<MonthlyAttendanceCountResponse>({
    queryKey: ['monthly-attendance-count', customer?.id, currentStoreId],
    queryFn: async () => {
      if (!customer?.id) throw new Error('Customer ID is required');
      const response = await apiClient.get<MonthlyAttendanceCountResponse>(
        `/attendance/monthly-count/${customer.id}`,
        { params: { store_id: currentStoreId } }
      );
      return response.data;
    },
    enabled: opened && !!customer?.id,
    staleTime: 1000 * 60,
  });

  const { data: contractData } = useQuery<CustomerContractResponse>({
    queryKey: ['customer-contract', customer?.id],
    queryFn: async () => {
      if (!customer?.id) throw new Error('Customer ID is required');
      const response = await apiClient.get<CustomerContractResponse>(
        `/attendance/contract/${customer.id}`
      );
      return response.data;
    },
    enabled: opened && !!customer?.id,
    staleTime: 1000 * 60,
  });

  // 今月の入退室履歴を全件取得（利用校基準）
  const { data: historyData, isLoading: isLoadingHistory } =
    useQuery<RecentAttendanceHistoryResponse>({
      queryKey: ['monthly-attendance-history', customer?.id, currentStoreId],
      queryFn: async () => {
        if (!customer?.id) throw new Error('Customer ID is required');
        const response = await apiClient.get<RecentAttendanceHistoryResponse>(
          `/attendance/recent-history/${customer.id}`,
          { params: { limit: 31, store_id: currentStoreId } }
        );
        return response.data;
      },
      enabled: opened && !!customer?.id,
      staleTime: 1000 * 60,
    });

  // 年齢計算
  const calculateAge = (birthDate: string | undefined): number | null => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // 滞在時間フォーマット
  const formatStayDuration = (duration: string | undefined): string => {
    if (!duration) return '';
    const durationStr = String(duration);
    if (durationStr.indexOf('時間') !== -1 || durationStr.indexOf('分') !== -1) {
      return durationStr;
    }
    if (durationStr.indexOf(':') !== -1) {
      const parts = durationStr.split(':');
      const hours = parseInt(parts[0] || '0', 10);
      const minutes = parseInt(parts[1] || '0', 10);
      if (hours > 0 && minutes > 0) {
        return `${hours}時間${minutes}分`;
      } else if (hours > 0) {
        return `${hours}時間`;
      } else if (minutes > 0) {
        return `${minutes}分`;
      }
    }
    const num = parseInt(durationStr, 10);
    if (!isNaN(num)) {
      if (num >= 60) {
        const hours = Math.floor(num / 60);
        const remainingMinutes = num % 60;
        return remainingMinutes > 0 ? `${hours}時間${remainingMinutes}分` : `${hours}時間`;
      } else {
        return `${num}分`;
      }
    }
    return durationStr;
  };

  // カウントダウン（localStorageから設定値を取得、デフォルト300秒）
  const getStoredCountdown = (): number => {
    const stored = localStorage.getItem('attendance_modal_countdown');
    return stored ? Math.max(5, Math.min(300, parseInt(stored, 10) || 300)) : 300;
  };

  const adjustCountdown = (delta: number) => {
    setCountdown((prev) => {
      if (prev === null) return null;
      const newVal = Math.max(5, Math.min(300, prev + delta));
      localStorage.setItem('attendance_modal_countdown', String(newVal));
      return newVal;
    });
  };

  // 自動クローズ無効 — 手動クローズまたは次のカードスキャンで上書き
  useEffect(() => {
    if (opened) {
      setCountdown(null);
    }
  }, [opened]);

  if (!customer) {
    return null;
  }

  const isCheckIn = attendance?.action === 'check_in';
  const isOvertime = attendance?.is_overtime || false;
  const isPaused = customer.status === 'paused';
  const isWithdrawn = customer.status === 'withdrawn';
  const isNotActive = isPaused || isWithdrawn;
  const isDifferentStore = currentStoreId && customer.store?.id !== currentStoreId;

  // 色の決定（優先順位: 休会/退会 > 時間外 > 入室/退室）
  const actionColor = isNotActive ? 'red' : isOvertime ? 'yellow' : isCheckIn ? 'green' : 'blue';
  const actionColorHex = isNotActive
    ? '#ef4444'
    : isOvertime
      ? '#f59e0b'
      : isCheckIn
        ? '#10b981'
        : '#3b82f6';
  const age = calculateAge(customer.birth_date);

  // 性別による枠色
  const getAvatarBorderColor = () => {
    if (customer.gender === 'male') return '#3b82f6';
    if (customer.gender === 'female') return '#ec4899';
    if (customer.gender === 'other') return '#8b5cf6';
    return actionColorHex;
  };

  const getAvatarGlow = () => {
    const color = getAvatarBorderColor();
    return `0 0 40px ${color}80, 0 0 60px ${color}40`;
  };

  // ステータス関連
  const getStatusLabel = () => {
    if (customer.status === 'active') return '在籍';
    if (customer.status === 'paused') return '休会中';
    return '退会済';
  };

  const getStatusColor = () => {
    if (customer.status === 'active') return 'green';
    if (customer.status === 'paused') return 'yellow';
    return 'red';
  };

  const memoHasMemo = customer.memo && customer.memo.trim() !== '';

  // パルス背景スタイル
  const pulseBackground = isNotActive
    ? 'radial-gradient(circle at center, rgba(239, 68, 68, 0.8) 0%, rgba(239, 68, 68, 0.5) 40%, rgba(239, 68, 68, 0.2) 70%)'
    : 'radial-gradient(circle at center, rgba(245, 158, 11, 0.8) 0%, rgba(245, 158, 11, 0.5) 40%, rgba(245, 158, 11, 0.2) 70%)';

  // 月間利用上限を取得（APIから直接 or プラン名からパース）
  const monthlyCount = monthlyData?.data?.count || 0;
  const overtimeCount = overtimeData?.data?.overtime_count || 0;

  // 月間利用上限（優先順位: monthly_limit > weekly_attendance_count * 4 > プラン名パース）
  const getMonthlyLimit = (): number => {
    // 直接設定された月間上限
    if (contractData?.data?.monthly_limit) {
      return contractData.data.monthly_limit;
    }
    // 週回数から計算
    if (contractData?.data?.weekly_attendance_count) {
      return contractData.data.weekly_attendance_count * 4;
    }
    // プラン名からパース（フォールバック）
    const planName = contractData?.data?.plan_name;
    if (planName) {
      const match = planName.match(/週(\d+)回/);
      if (match) return parseInt(match[1], 10) * 4;
    }
    return 0;
  };

  const monthlyLimit = getMonthlyLimit();
  const remainingCount = monthlyLimit > 0 ? Math.max(monthlyLimit - monthlyCount, 0) : 0;
  const progressPercent = monthlyLimit > 0 ? Math.min((monthlyCount / monthlyLimit) * 100, 100) : 0;
  const isOverLimit = monthlyLimit > 0 && monthlyCount >= monthlyLimit;

  return (
    <>
      {/* パルスエフェクト背景（時間外または休会/退会中） */}
      <AnimatePresence>
        {opened && (isOvertime || isNotActive) && (
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: pulseBackground,
              pointerEvents: 'none',
              zIndex: 200,
            }}
          />
        )}
      </AnimatePresence>

      <Modal
        opened={opened}
        onClose={onClose}
        centered
        size="90%"
        padding="lg"
        keepMounted={false}
        withCloseButton={false}
        styles={{
          overlay: {
            backgroundColor: isNotActive
              ? 'rgba(239, 68, 68, 0.3)'
              : isOvertime
                ? 'rgba(245, 158, 11, 0.3)'
                : isCheckIn
                  ? 'rgba(16, 185, 129, 0.3)'
                  : 'rgba(59, 130, 246, 0.3)',
            backdropFilter: 'blur(16px)',
          },
          content: {
            maxHeight: '90vh',
            overflow: 'visible',
          },
          body: {
            padding: 0,
            overflow: 'hidden',
          },
        }}
      >
        <ScrollArea h="calc(90vh - 40px)" offsetScrollbars>
          <Box p="md">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Stack gap="md">
                {/* ヘッダー */}
                <Group justify="flex-end">
                  <Button variant="subtle" color={actionColor} onClick={onClose} size="sm">
                    ×
                  </Button>
                </Group>

                {/* 3カラムレイアウト */}
                <Grid gutter="md">
                  {/* 左カラム: KPI情報 */}
                  <Grid.Col span={3}>
                    <Stack gap="md">
                      {/* 入室/退室時刻 */}
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                      >
                        <Paper
                          p="md"
                          radius="md"
                          withBorder
                          style={{
                            borderColor: actionColorHex,
                            borderWidth: 2,
                            background: `linear-gradient(135deg, ${actionColorHex}20 0%, ${actionColorHex}05 100%)`,
                          }}
                        >
                          {isCheckIn ? (
                            /* 入室時: 入室時間のみ大きく表示 */
                            <Stack gap="xs">
                              <Group gap="xs">
                                <IconLogin size={20} color={actionColorHex} />
                                <Text fw={600} size="sm">
                                  入室
                                </Text>
                              </Group>
                              <Text size="2.5rem" fw={800} c={actionColor} ta="center">
                                {attendance?.check_in_time}
                              </Text>
                            </Stack>
                          ) : (
                            /* 退室時: 入室→退室と滞在時間を表示 */
                            <Stack gap="sm">
                              <Group gap="xs">
                                <IconLogout size={20} color={actionColorHex} />
                                <Text fw={600} size="sm">
                                  退室
                                </Text>
                              </Group>
                              {/* 入室 → 退室 時間 */}
                              <Group justify="center" align="center" gap="xs">
                                <Stack gap={0} align="center">
                                  <Text size="xs" c="dimmed">
                                    入室
                                  </Text>
                                  <Text size="lg" fw={600} c="dimmed">
                                    {attendance?.check_in_time}
                                  </Text>
                                </Stack>
                                <Text size="xl" c="dimmed">
                                  →
                                </Text>
                                <Stack gap={0} align="center">
                                  <Text size="xs" c={actionColor}>
                                    退室
                                  </Text>
                                  <Text size="xl" fw={800} c={actionColor}>
                                    {attendance?.check_out_time}
                                  </Text>
                                </Stack>
                              </Group>
                              {/* 滞在時間（大きく表示） */}
                              {attendance?.stay_duration && (
                                <Paper
                                  p="xs"
                                  radius="md"
                                  style={{
                                    background: `linear-gradient(135deg, ${actionColorHex}30 0%, ${actionColorHex}15 100%)`,
                                    border: `1px solid ${actionColorHex}50`,
                                  }}
                                >
                                  <Group justify="center" gap="xs">
                                    <IconClock size={20} color={actionColorHex} />
                                    <Text size="xl" fw={700} c={actionColor}>
                                      {formatStayDuration(attendance.stay_duration)}
                                    </Text>
                                  </Group>
                                </Paper>
                              )}
                            </Stack>
                          )}
                        </Paper>
                      </motion.div>

                      {/* 今月の出席回数（残り回数表示付き） */}
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                      >
                        <Paper
                          p="md"
                          radius="md"
                          withBorder
                          style={{
                            borderColor: isOverLimit ? '#ef4444' : actionColorHex,
                            borderWidth: isOverLimit ? 3 : 2,
                            background: isOverLimit
                              ? 'linear-gradient(135deg, #ef444425 0%, #ef444410 100%)'
                              : `linear-gradient(135deg, ${actionColorHex}15 0%, ${actionColorHex}05 100%)`,
                          }}
                        >
                          <Stack gap="sm">
                            <Group gap="xs">
                              <IconCalendar
                                size={20}
                                color={isOverLimit ? '#ef4444' : actionColorHex}
                              />
                              <Text fw={600} size="sm">
                                今月の利用
                              </Text>
                            </Group>
                            {monthlyData?.data ? (
                              <>
                                {monthlyLimit > 0 ? (
                                  <>
                                    {/* 回数制限あり: 残り回数を大きく表示 */}
                                    <Stack gap={4} align="center">
                                      <Group justify="center" align="baseline" gap={4}>
                                        <Text size="md" c="dimmed">
                                          残り
                                        </Text>
                                        <Text
                                          size="3rem"
                                          fw={800}
                                          c={
                                            remainingCount === 0
                                              ? 'red'
                                              : remainingCount <= 2
                                                ? 'orange'
                                                : actionColor
                                          }
                                        >
                                          {remainingCount}
                                        </Text>
                                        <Text size="lg" c="dimmed">
                                          回
                                        </Text>
                                      </Group>
                                      <Text size="sm" c="dimmed">
                                        ({monthlyCount} / {monthlyLimit}回 使用済)
                                      </Text>
                                    </Stack>
                                    <Progress
                                      value={progressPercent}
                                      color={
                                        progressPercent >= 100
                                          ? 'red'
                                          : progressPercent >= 75
                                            ? 'orange'
                                            : actionColor
                                      }
                                      size="lg"
                                      radius="xl"
                                      striped
                                      animated={progressPercent < 100}
                                    />
                                    {isOverLimit && (
                                      <Badge color="red" variant="filled" size="sm">
                                        上限に達しました
                                      </Badge>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {/* 回数制限なし: 利用回数のみ表示 */}
                                    <Group justify="center" align="baseline" gap={4}>
                                      <Text size="2.5rem" fw={800} c={actionColor}>
                                        {monthlyCount}
                                      </Text>
                                      <Text size="lg" c="dimmed">
                                        回
                                      </Text>
                                    </Group>
                                    <Badge color="gray" variant="light" size="sm">
                                      回数制限なし
                                    </Badge>
                                  </>
                                )}
                              </>
                            ) : (
                              <Center py="md">
                                <Loader size="md" color={actionColor} />
                              </Center>
                            )}
                          </Stack>
                        </Paper>
                      </motion.div>

                      {/* 時間外利用（視覚的表示） */}
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4, duration: 0.4 }}
                      >
                        <Paper
                          p="md"
                          radius="md"
                          withBorder
                          style={{
                            borderColor: overtimeCount > 0 ? '#f59e0b' : actionColorHex,
                            borderWidth: overtimeCount > 0 ? 3 : 2,
                            background:
                              overtimeCount > 0
                                ? 'linear-gradient(135deg, #f59e0b25 0%, #f59e0b10 100%)'
                                : `linear-gradient(135deg, ${actionColorHex}15 0%, ${actionColorHex}05 100%)`,
                          }}
                        >
                          <Stack gap="sm">
                            <Group gap="xs">
                              <IconClock
                                size={20}
                                color={overtimeCount > 0 ? '#f59e0b' : actionColorHex}
                              />
                              <Text fw={600} size="sm">
                                時間外利用
                              </Text>
                            </Group>
                            {overtimeData?.data ? (
                              <Group justify="center" align="center" gap="md">
                                <RingProgress
                                  size={80}
                                  thickness={8}
                                  roundCaps
                                  sections={[
                                    {
                                      value: Math.min(overtimeCount * 20, 100),
                                      color: overtimeCount > 0 ? 'orange' : 'gray',
                                    },
                                  ]}
                                  label={
                                    <Center>
                                      <Text
                                        size="lg"
                                        fw={700}
                                        c={overtimeCount > 0 ? 'orange' : 'dimmed'}
                                      >
                                        {overtimeCount}
                                      </Text>
                                    </Center>
                                  }
                                />
                                <Stack gap={4}>
                                  <Text
                                    size="xl"
                                    fw={700}
                                    c={overtimeCount > 0 ? 'orange' : 'dimmed'}
                                  >
                                    {overtimeCount}回
                                  </Text>
                                  {isOvertime && (
                                    <Badge color="orange" variant="filled" size="sm">
                                      今回も時間外！
                                    </Badge>
                                  )}
                                </Stack>
                              </Group>
                            ) : (
                              <Center py="md">
                                <Loader size="md" color={actionColor} />
                              </Center>
                            )}
                          </Stack>
                        </Paper>
                      </motion.div>

                      {/* 契約プラン */}
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5, duration: 0.4 }}
                      >
                        <Paper
                          p="md"
                          radius="md"
                          withBorder
                          style={{
                            borderColor: actionColorHex,
                            borderWidth: 2,
                            background: `linear-gradient(135deg, ${actionColorHex}15 0%, ${actionColorHex}05 100%)`,
                          }}
                        >
                          <Stack gap="sm">
                            <Group gap="xs">
                              <IconCreditCard size={20} color={actionColorHex} />
                              <Text fw={600} size="sm">
                                契約プラン
                              </Text>
                            </Group>
                            {contractData?.data?.has_contract ? (
                              <Stack gap={4} align="center">
                                <Text size="lg" fw={700} ta="center">
                                  {contractData.data.plan_name ||
                                    contractData.data.program_name ||
                                    '未設定'}
                                </Text>
                                <Text size="md" c="dimmed">
                                  ¥{contractData.data.monthly_fee?.toLocaleString() || 0}/月
                                </Text>
                                {monthlyLimit > 0 && (
                                  <Badge color={actionColor} variant="light">
                                    月{monthlyLimit}回まで
                                  </Badge>
                                )}
                              </Stack>
                            ) : (
                              <Text size="sm" c="dimmed" ta="center">
                                契約情報なし
                              </Text>
                            )}
                          </Stack>
                        </Paper>
                      </motion.div>
                    </Stack>
                  </Grid.Col>

                  {/* 中央カラム: 顧客情報 */}
                  <Grid.Col span={6}>
                    <Stack gap="md" align="center">
                      {/* 顔写真（上部はみ出し） */}
                      <motion.div
                        initial={{ y: isCheckIn ? -150 : 50, scale: 0.8, opacity: 0 }}
                        animate={{ y: 0, scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', duration: 0.8 }}
                        style={{
                          marginTop: 0,
                          position: 'relative',
                          zIndex: 10,
                        }}
                      >
                        <motion.div
                          animate={isOvertime || isNotActive ? { x: [-3, 3, -3, 3, 0] } : {}}
                          transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                        >
                          <Avatar
                            src={customer.photo_url || undefined}
                            alt={customer.name}
                            size={240}
                            radius="50%"
                            style={{
                              border: `8px solid ${getAvatarBorderColor()}`,
                              boxShadow: getAvatarGlow(),
                              backgroundColor: 'white',
                            }}
                          >
                            <IconUser size={120} />
                          </Avatar>
                        </motion.div>
                      </motion.div>

                      {/* 店舗（他店舗の場合は警告表示） */}
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.3 }}
                      >
                        {isDifferentStore ? (
                          <Paper
                            p="xs"
                            radius="md"
                            style={{
                              background: 'linear-gradient(135deg, #f59e0b30 0%, #f59e0b15 100%)',
                              border: '2px solid #f59e0b',
                            }}
                          >
                            <Group gap="xs" justify="center">
                              <IconAlertTriangle size={18} color="#f59e0b" />
                              <Text fw={700} size="sm" c="orange">
                                他店舗所属
                              </Text>
                              <Badge color="orange" variant="filled" size="lg">
                                {customer.store?.name || '未設定'}
                              </Badge>
                            </Group>
                          </Paper>
                        ) : (
                          <Badge
                            color={actionColor}
                            variant="light"
                            size="xl"
                            leftSection={<IconHome size={16} />}
                          >
                            {customer.store?.name || '未設定'}
                          </Badge>
                        )}
                      </motion.div>

                      {/* ID + ステータス */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.35, duration: 0.3 }}
                      >
                        <Group gap="sm" justify="center">
                          <Badge color={actionColor} variant="filled" size="xl">
                            #{customer.id}
                          </Badge>
                          {isNotActive ? (
                            <Paper
                              p="xs"
                              radius="md"
                              style={{
                                background: isPaused
                                  ? 'linear-gradient(135deg, #eab30830 0%, #eab30815 100%)'
                                  : 'linear-gradient(135deg, #ef444430 0%, #ef444415 100%)',
                                border: `2px solid ${isPaused ? '#eab308' : '#ef4444'}`,
                              }}
                            >
                              <Group gap="xs">
                                <IconExclamationCircle
                                  size={18}
                                  color={isPaused ? '#eab308' : '#ef4444'}
                                />
                                <Text fw={700} size="md" c={isPaused ? 'yellow' : 'red'}>
                                  {getStatusLabel()}
                                </Text>
                              </Group>
                            </Paper>
                          ) : (
                            <Badge color={getStatusColor()} variant="filled" size="xl">
                              {getStatusLabel()}
                            </Badge>
                          )}
                        </Group>
                      </motion.div>

                      {/* 氏名・カナ */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.3 }}
                      >
                        <Stack gap={2} align="center">
                          <Text size="2.5rem" fw={800} lh={1.2} ta="center">
                            {customer.name}
                          </Text>
                          <Text size="lg" c="dimmed">
                            {customer.name_kana}
                          </Text>
                        </Stack>
                      </motion.div>

                      {/* 生年月日・年齢・性別 */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.3 }}
                      >
                        <Group gap="md" justify="center">
                          <Text size="md" fw={500}>
                            {customer.birth_date ? formatDate(customer.birth_date) : '未登録'}
                          </Text>
                          {age !== null && (
                            <Badge color={actionColor} variant="light" size="lg">
                              {age}歳
                            </Badge>
                          )}
                          <Badge
                            color={
                              customer.gender === 'male'
                                ? 'blue'
                                : customer.gender === 'female'
                                  ? 'pink'
                                  : 'purple'
                            }
                            variant="light"
                            size="lg"
                          >
                            {customer.gender === 'male'
                              ? '男性'
                              : customer.gender === 'female'
                                ? '女性'
                                : 'その他'}
                          </Badge>
                        </Group>
                      </motion.div>

                      {/* メモ（ある場合のみ） */}
                      <AnimatePresence>
                        {memoHasMemo && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ delay: 0.6, duration: 0.5 }}
                            style={{ width: '100%', maxWidth: 500 }}
                          >
                            <motion.div
                              animate={{
                                boxShadow: [
                                  '0 0 20px rgba(245, 158, 11, 0.3)',
                                  '0 0 40px rgba(245, 158, 11, 0.6)',
                                  '0 0 20px rgba(245, 158, 11, 0.3)',
                                ],
                              }}
                              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                            >
                              <Paper
                                p="md"
                                radius="md"
                                withBorder
                                style={{
                                  borderColor: '#f59e0b',
                                  borderWidth: 2,
                                  background:
                                    'linear-gradient(135deg, #f59e0b20 0%, #f59e0b10 100%)',
                                }}
                              >
                                <Stack gap="xs">
                                  <Group gap="xs">
                                    <ThemeIcon color="orange" variant="light" size="md">
                                      <IconAlertCircle size={18} />
                                    </ThemeIcon>
                                    <Text fw={700} size="sm" c="orange">
                                      メモ・注意事項
                                    </Text>
                                  </Group>
                                  <Text size="md" style={{ whiteSpace: 'pre-line' }}>
                                    {customer.memo}
                                  </Text>
                                </Stack>
                              </Paper>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* 閉じるボタン */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.7, duration: 0.3 }}
                      >
                        <Button
                          size="xl"
                          color={actionColor}
                          leftSection={
                            isCheckIn ? <IconLogin size={24} /> : <IconLogout size={24} />
                          }
                          onClick={onClose}
                          style={{ minWidth: 250 }}
                        >
                          閉じる
                        </Button>
                      </motion.div>
                    </Stack>
                  </Grid.Col>

                  {/* 右カラム: 入退室履歴 */}
                  <Grid.Col span={3}>
                    <motion.div
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3, duration: 0.4 }}
                      style={{ height: '100%' }}
                    >
                      <Paper
                        p="md"
                        radius="md"
                        withBorder
                        style={{
                          borderColor: actionColorHex,
                          borderWidth: 2,
                          background: `linear-gradient(135deg, ${actionColorHex}10 0%, ${actionColorHex}05 100%)`,
                          height: '100%',
                        }}
                      >
                        <Stack gap="sm" h="100%">
                          <Group gap="xs">
                            <IconCalendarTime size={20} color={actionColorHex} />
                            <Text fw={600} size="sm">
                              今月の入退室履歴
                            </Text>
                          </Group>

                          {isLoadingHistory ? (
                            <Center py="xl" style={{ flex: 1 }}>
                              <Loader size="lg" color={actionColor} />
                            </Center>
                          ) : historyData?.data?.attendances &&
                            historyData.data.attendances.length > 0 ? (
                            <ScrollArea style={{ flex: 1, maxHeight: '50vh' }} offsetScrollbars>
                              <Stack gap={6}>
                                {historyData.data.attendances.map((history, index) => (
                                  <motion.div
                                    key={history.id}
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.4 + index * 0.05, duration: 0.2 }}
                                  >
                                    <Paper
                                      p="xs"
                                      radius="sm"
                                      style={{
                                        backgroundColor:
                                          index % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
                                      }}
                                    >
                                      <Group justify="space-between">
                                        <Text size="sm" fw={600}>
                                          {history.date_formatted}
                                        </Text>
                                        <Stack gap={2}>
                                          {history.check_in_time && (
                                            <Badge color="green" variant="light" size="sm">
                                              入 {history.check_in_time}
                                            </Badge>
                                          )}
                                          {history.check_out_time && (
                                            <Badge color="blue" variant="light" size="sm">
                                              退 {history.check_out_time}
                                            </Badge>
                                          )}
                                        </Stack>
                                      </Group>
                                    </Paper>
                                  </motion.div>
                                ))}
                              </Stack>
                            </ScrollArea>
                          ) : (
                            <Center py="xl" style={{ flex: 1 }}>
                              <Text size="sm" c="dimmed" ta="center">
                                今月の入退室履歴がありません
                              </Text>
                            </Center>
                          )}
                        </Stack>
                      </Paper>
                    </motion.div>
                  </Grid.Col>
                </Grid>
              </Stack>
            </motion.div>
          </Box>
        </ScrollArea>
      </Modal>
    </>
  );
};
